import React, { useState, useEffect } from "react";
import { Modal, Form, Alert, OverlayTrigger, Tooltip } from "react-bootstrap";

import { updateSetting, addSetting} from "../../../../services/api/settings-api";
import DropdownMain from "../../../../components/dropdown-main/DropdownMain";

import './super-admin-settings-editor.scss';

const SettingForm = (props) => {
  let {
    show,
    handleClose,
    handleUpdate,
    handleAdd,
    currSetting,
    action,
    allDepartments,
    allUsers,
    canEdit = true
  } = props;

  const isViewMode = action === "view" || (action === "update" && !canEdit);

  const [setting, setSetting] = useState({
    Id: currSetting.Id,
    Name: currSetting.Name,
    CompanyId: currSetting.CompanyId,
    UserId: currSetting.UserId,
    Value: currSetting.Value,
    SubDepartment: currSetting.SubDepartment
  });

  const blankDepartment = {
    Id:0,
    Name: "",
    Active: 0
  }

  const blankUser = {
    Id: 0,
    Name: "",
    Active: 0,
    Initials: "NA"
  }
  
  const [selectedDepartment, setSelectedDepartment] = useState({});
  const [selectedUser, setSelectedUser] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const resetStates = () => {
    setSelectedDepartment({...blankDepartment});
    setSelectedUser({...blankUser});
  }

  const handleSettingUpdate = () => {
    const result = updateSetting(setting);
    result.then(()=>{
      // display the updation message
      resetStates();
      handleUpdate(setting);    
    })
    .catch((error) =>{
      // display the error message
      setAlertMessage("Update failed !");
      setShowAlert(true);

      console.log(error);
    })
  };

  const handleSettingAdd = () => {
    const result = addSetting(setting);
    result.then((response)=>{
      resetStates();
      handleAdd(response);
    })
    .catch((error) =>{
      // display the error message
      setAlertMessage("Could not create new setting !");
      setShowAlert(true);

      console.log(error);
    });
  };

  const handleValueChange = (event) => {
    const value = event.target.value;
    const field = event.target.getAttribute("field");

    setSetting((prevSetting) => {
      return {
        ...prevSetting,
        [field]: value
      };
    });
  };
  
  const handleSelectedDepartment = (selection) => {
    setSelectedDepartment(selection);
    setSetting((prevSetting) => {
      return {
        ...prevSetting,
        CompanyId: selection.id
      };
    });
  }

  const handleSelectedUser = (selection) => {
    setSelectedUser(selection);

    setSetting((prevSetting) => {
      return {
        ...prevSetting,
        UserId: selection.id
      };
    });
  }

  const handleButtonClose = () => {
    resetStates();
    handleClose();
  }

  const displayAlert = () => {
    const handleEnd = () => {
      setShowAlert(false);
      setAlertMessage("");
    }

    window.setTimeout(()=>{handleEnd()}, 4000);

    return (
      <Alert show={showAlert} onClose={() => handleEnd()} variant="danger" dismissible>
        {alertMessage}
      </Alert>
    );
  }

  useEffect(() => {
    const existingDepartment = allDepartments.find(department => department.Id === currSetting.CompanyId);
    const existingUser = allUsers.find(user => user.Id === currSetting.UserId);
    setSetting({ ...currSetting });
    
    if(existingDepartment){
      const departmentName = existingDepartment.Name 
        + (existingDepartment.Id !== 0? (", [ " + existingDepartment.Id + " ]") : "")
        + (existingDepartment.Active ? "" : " [Inactive]");

      setSelectedDepartment({ 
        id: existingDepartment.Id, 
        name: departmentName,
        disabled: !existingDepartment.Active 
      });
    }
    
    if(existingUser){
      const userName = existingUser.Name 
        + (existingUser.Email? (", "+ existingUser.Email) : "" ) 
        + (existingUser.Id !== 0?  (", [ "+ existingUser.Id + " ]") : "" )
        + (existingUser.Active ? "" : " [Inactive]");

      setSelectedUser({ 
        id: existingUser.Id, 
        name: userName,
        disabled: !existingUser.Active, 
        Initials: existingUser.Initials
      });
    }
  }, [currSetting, allDepartments, allUsers]);

  const getModalTitle = () => {
    if (isViewMode) return "View Setting";
    if (action === "update") return "Update Setting";
    return "Add Setting";
  };

  return (
    <Modal show={show}>
      <Modal.Header>
        <Modal.Title>
          {getModalTitle()}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {showAlert ? displayAlert(): <></>}
        <Form>
          <Form.Group className="mb-3" controlId="formBasicEmail">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              field="Name"
              value={setting.Name}
              onChange={(e) => {
                handleValueChange(e);
              }}
              placeholder="Enter name"
              disabled={isViewMode}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formBasicPassword">
            <Form.Label>Value</Form.Label>
            <Form.Control
              type="text"
              field="Value"
              value={setting.Value}
              onChange={(e) => {
                handleValueChange(e);
              }}
              placeholder="Enter value"
              disabled={isViewMode}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formBasicPassword">
            <Form.Label>Company ID</Form.Label>
            <DropdownMain
              values={allDepartments && allDepartments.map((department) => {
                  const departmentName = department.Name 
                    + (department.Id !== 0 ? (", [ " + department.Id + " ]") : "")
                    + (department.Active ? "" : " [Inactive]");
                  return { id: department.Id, name: departmentName, disabled: !department.Active }
              })}
              dropdownButtonText={"Select a Company"}
              onSelect={(selectedValue) => {
                handleSelectedDepartment(selectedValue);
              }}
              currentSelected={selectedDepartment}
              disabled={isViewMode}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formBasicPassword">
            <Form.Label>User ID</Form.Label>
            <DropdownMain
              values = {
                allUsers && allUsers.map((member) => {
                  const userName = member.Name 
                    + (member.Email? (", "+ member.Email) : "" ) 
                    + (member.Id !== 0 ?  (", [ " + member.Id + " ]") : "" )
                    + (member.Active ? "" : " [Inactive]");
                  return { id: member.Id, name: userName, disabled: !member.Active, Initials: member.Initials }
                })
              }
              dropdownButtonText={"Select a User"}
              onSelect={(selectedValue) => {
                handleSelectedUser(selectedValue);
              }}
              currentSelected={selectedUser}
              settings={true}
              disabled={isViewMode}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formBasicPassword">
            <Form.Label>Sub-Department</Form.Label>
            <Form.Control
              type="SubDepartment"
              field="SubDepartment"
              value={setting.SubDepartment}
              onChange={(e) => {
                handleValueChange(e);
              }}
              placeholder="Enter sub-department"
              disabled={isViewMode}
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <button className="btn btn-setting-light btn-sm" onClick={handleButtonClose}>Cancel</button>
        {action === "update" || action === "view" ? (
          isViewMode ? (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tooltip-update-disabled">You do not have the requisite Role Privileges to Edit Setting Objects on TRIYO.</Tooltip>}
            >
              <span className="d-inline-block">
                <button 
                  className="btn btn-setting-primary btn-sm" 
                  disabled
                  style={{ pointerEvents: 'none' }}
                >
                  Update
                </button>
              </span>
            </OverlayTrigger>
          ) : (
            <button className="btn btn-setting-primary btn-sm" onClick={handleSettingUpdate}>Update</button>
          )
        ) : (
          <button className="btn btn-setting-primary btn-sm" onClick={handleSettingAdd}>Add</button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default SettingForm;
   